import type {
  CellClassParams,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  ICellEditorComp,
  ICellEditorParams,
  ICellRendererComp,
  ICellRendererParams,
  ILoadingCellRendererParams,
  TooltipCallbackParams,
  ValueFormatterParams
} from 'ag-grid-community'
import {
  filter,
  flatMap,
  fromEntries,
  hasProp,
  isPlainObject,
  isString,
  map,
  pipe
} from 'remeda'
import {
  formatDate,
  readCompanyFormatSettings
} from '../../../helpers/formatters'
import type { ViewHook } from '../../../helpers/utils'
import { buildOptionPagePayload } from '../../combobox/model'
import { isOptionPageReply, pushEventWithCallback } from '../../combobox/utils'
import { BadgeComponent } from '../components/badge'
import { SimpleBadgeDropdownRenderer } from '../components/badge-rich-select-renderer'
import { BadgesComponent } from '../components/badges'
import { ButtonComponent } from '../components/button'
import {
  CellPlaceholder,
  type CellPlaceholderParams
} from '../components/cell-placeholder'
import { HeaderComponent } from '../components/header'
import { IconComponent } from '../components/icon'
import { IconTextComponent } from '../components/icon-text'
import { ImageComponent } from '../components/image'
import { MarkdownComponent } from '../components/markdown'
import { ProgressComponent } from '../components/progress'
import { RedirectComponent } from '../components/redirect'
import { RowDraggerComponent } from '../components/row-dragger'
import { StepperComponent } from '../components/stepper'
import { StepperRichSelectRenderer } from '../components/stepper-rich-select-renderer'
import {
  AG_EDITORS,
  DEFERRED_RENDER_FORMATTERS,
  EDITOR_TYPES,
  type EditorType,
  FORMATTER_TYPES,
  ROW_POSITIONS
} from '../constants'
import editorMinWidths from '../constants/editor-min-widths.json' with {
  type: 'json'
}
import { FacadeTelCellEditor } from '../editors/tel'
import {
  FacadeDateCellEditor,
  FacadeDateTimeCellEditor,
  FacadeTimeCellEditor
} from '../editors/temporal'
import {
  extractEditorDisplayValue,
  extractEditorValue,
  isEditorValueCell,
  isParsedEditorValue,
  isRawDisplayCell,
  parsedEditorValue,
  parseRichSelectEditorValue,
  readStagedEditorValue,
  setEditorCellValue
} from './editor-value'
import { createFastRenderer, escapeHTML } from './fast-renderer'
import { extractHighlight, getCellHighlightStyle } from './highlight'
import { readArrayPath } from './object-path'
import type { GridRuntime } from './runtime'
import { gridSortField } from './sorting-state'
import { createTextValues, type StepperValueItem } from './transforms'

type EditorScalar = string | number | boolean

// Editor parameter value structure for complex data
type EditorParamValue = {
  readonly icon?: string
  readonly color?: string
  readonly title?: string
  readonly label?: string
  readonly value: EditorScalar
}

type ClearEditorOption = {
  readonly label: '—'
  readonly value: null
}

type RichSelectOption = EditorParamValue | ClearEditorOption

// AG Grid otherwise constrains the popup to the edited cell's width.
const SELECT_LIST_WIDTH =
  'min(var(--spacing-96), calc(100vw - var(--spacing-8)))'

const EDITOR_MIN_WIDTHS: Readonly<Partial<Record<EditorType, number>>> =
  editorMinWidths

type GridEditorParams = {
  readonly multiple?: boolean
  readonly required?: boolean
  readonly min?: string | number
  readonly max?: string | number
  readonly precision?: 'minute' | 'second' | 'millisecond' | number
  readonly values?: readonly EditorParamValue[]
  readonly loadEvent?: string
  readonly loadParams?: Readonly<Record<string, unknown>>
  readonly pageSize?: number
  readonly timeout?: number
  readonly country?: string
  readonly locale?: string
  readonly onlyCountries?: readonly string[] | string
}

type GridEditorRuntimeContext = {
  readonly hook?: ViewHook
  readonly target?: string | null
  readonly runtime?: GridRuntime
}

type GridEditorCellParams = ICellEditorParams<
  Record<string, unknown>,
  unknown,
  GridEditorRuntimeContext
>

// Badge value item structure (from backend)
type BadgeValueItem = {
  readonly value: EditorScalar
  readonly label: string
  readonly icon?: string
  readonly color?: string
}

// Badge cell data structure (from backend)
type BadgeCellData = {
  readonly value: EditorScalar | null
  readonly label: string
  readonly icon?: string
  readonly color?: string
  readonly editable?: boolean
  readonly editorParams?: {
    readonly values?: readonly BadgeValueItem[]
  }
}

// Grid leaf column configuration with comprehensive typing
type GridLeafColumn = {
  readonly sortField?: string
  readonly field: string
  readonly title: string
  readonly align?: 'right'
  readonly width?: number
  readonly minWidth?: number
  readonly maxWidth?: number
  readonly flex?: number
  readonly pinned?: 'left' | 'right'
  readonly visible: boolean
  readonly formatter: string
  readonly editor?: EditorType
  readonly editable?: boolean
  readonly editorParams?: GridEditorParams
  readonly titleFormatterParams?: {
    readonly icon?: string
  }
  readonly bottomCalc?: string
  readonly bottomCalcParams?: {
    readonly value?: string | number
  }
  readonly bottomCalcFormatterParams?: {
    readonly tooltip?: string | null
  }
}

type GridGroupColumn = {
  readonly title: string
  readonly groupId?: string
  readonly children: readonly GridColumn[]
}

type GridColumn = GridLeafColumn | GridGroupColumn

export const isGridGroupColumn = (
  column: GridColumn
): column is GridGroupColumn => 'children' in column

export const flattenGridColumns = (
  columns: readonly GridColumn[]
): readonly GridLeafColumn[] =>
  pipe(
    columns,
    flatMap((column) =>
      isGridGroupColumn(column) ? flattenGridColumns(column.children) : [column]
    )
  )

// Cell editor params function type
type CellEditorParamsFunction = (
  params: GridEditorCellParams
) => Record<string, unknown>

type CellEditorComponent =
  | string
  | (new () => ICellEditorComp<Record<string, unknown>, unknown>)

// Cell renderer selectors require constructable AG Grid components.
type CellRendererSelection = {
  readonly component: new () => ICellRendererComp
}

// Cell renderer selector function type
type CellRendererSelectorFunction = (
  params: ICellRendererParams
) => CellRendererSelection

// Tooltip value getter function type
type TooltipValueGetter = (
  params: TooltipCallbackParams<Record<string, unknown>, unknown, unknown>
) => string | null

// Header component params structure
type HeaderComponentParams = {
  readonly innerHeaderComponent: typeof HeaderComponent
  readonly innerHeaderComponentParams: {
    readonly icon?: string
  }
}

// Object with value property for complex cell data
type ObjectWithValue = {
  readonly value: unknown
}

type ObjectWithFormatter = {
  readonly formatter: string
}

type GridReadonlyContext = {
  readonly readonly?: boolean
}

type EditableCellParams = EditableCallbackParams<
  Record<string, unknown>,
  unknown,
  GridReadonlyContext
>

type EditableCellClassParams = CellClassParams<
  Record<string, unknown>,
  unknown,
  GridReadonlyContext
>

/** Formatter type guards - kept for backward compatibility in other modules */
export const isBadgeFormatter = (formatter: string): boolean =>
  formatter === FORMATTER_TYPES.BADGE

const hasValueProperty = (value: unknown): value is ObjectWithValue =>
  isPlainObject(value) && hasProp(value, 'value')

const hasFormatterProperty = (value: unknown): value is ObjectWithFormatter =>
  isPlainObject(value) &&
  hasProp(value, 'formatter') &&
  isString(value.formatter)

export const resolveCellFormatter = (
  columnFormatter: string,
  cellValue: unknown
): string =>
  columnFormatter === FORMATTER_TYPES.MIXED && hasFormatterProperty(cellValue)
    ? cellValue.formatter
    : columnFormatter === FORMATTER_TYPES.MIXED
      ? FORMATTER_TYPES.PLAINTEXT
      : columnFormatter

/** Editor type guards - internal helpers for editor detection */
const isNumberEditor = (editor?: string): boolean =>
  editor === EDITOR_TYPES.NUMBER
const isDateEditor = (editor?: string): boolean => editor === EDITOR_TYPES.DATE
const isStepperEditor = (editor?: string): boolean =>
  editor === EDITOR_TYPES.STEPPER
const isDropdownEditor = (editor?: string): boolean =>
  editor === EDITOR_TYPES.DROPDOWN
const isSelectEditor = (editor?: string): boolean =>
  editor === EDITOR_TYPES.SELECT || editor === EDITOR_TYPES.LIST
const isComboboxEditor = (editor?: string): boolean =>
  editor === EDITOR_TYPES.COMBOBOX
const isTagsEditor = (editor?: string): boolean => editor === EDITOR_TYPES.TAGS

/** Unified rich select editor type guard for stepper and dropdown editors */
export const isRichSelectEditor = (
  editor?: string,
  params?: GridEditorParams
): boolean =>
  isStepperEditor(editor) ||
  isDropdownEditor(editor) ||
  isComboboxEditor(editor) ||
  isTagsEditor(editor) ||
  (isSelectEditor(editor) && params?.multiple === true)

/**
 * Text cell renderer component implementing AG Grid's constructor contract
 * Uses createFastRenderer for optimized scroll performance
 */
export const TextComponent = createFastRenderer<unknown>((data, params) =>
  escapeHTML(
    params.valueFormatted ?? (hasValueProperty(data) ? data.value : data)
  )
)

/**
 * Number cell renderer component implementing AG Grid's constructor contract
 * Uses createFastRenderer for optimized scroll performance with locale-aware formatting
 */
/**
 * Get the number of decimal places from a value string
 */
const getDecimalPlaces = (value: string): number => {
  const decimalIndex = value.indexOf('.')
  return decimalIndex === -1 ? 0 : value.length - decimalIndex - 1
}

export const NumberComponent = createFastRenderer<unknown>((data) => {
  let value = data

  // If value is an object, extract the value property with type safety
  if (hasValueProperty(value)) {
    value = value.value
  }

  if (value === null || value === undefined || value === '') {
    return ''
  }

  const strValue = String(value)
  const num = typeof value === 'number' ? value : Number.parseFloat(strValue)

  if (Number.isNaN(num)) {
    return escapeHTML(strValue)
  }

  // Detect decimal places from the original value to preserve precision
  const decimalPlaces = getDecimalPlaces(strValue)

  // Use Intl.NumberFormat for proper locale-aware number formatting
  return new Intl.NumberFormat(readCompanyFormatSettings().locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.max(decimalPlaces, 2)
  }).format(num)
})

/**
 * Select cell renderer based on formatter type
 * Returns class constructors accepted by AG Grid's component registry
 */
export const selectCellRenderer = (
  formatter: string
): (new () => ICellRendererComp) => {
  switch (formatter) {
    case FORMATTER_TYPES.STEPPER:
      return StepperComponent
    case FORMATTER_TYPES.BADGE:
      return BadgeComponent
    case FORMATTER_TYPES.BADGES:
      return BadgesComponent
    case FORMATTER_TYPES.ICON:
      return IconComponent
    case FORMATTER_TYPES.ICON_TEXT:
      return IconTextComponent
    case FORMATTER_TYPES.PROGRESS:
      return ProgressComponent
    case FORMATTER_TYPES.REDIRECT:
      return RedirectComponent
    case FORMATTER_TYPES.BUTTON:
      return ButtonComponent
    case FORMATTER_TYPES.IMAGE:
      return ImageComponent
    case FORMATTER_TYPES.MARKDOWN:
      return MarkdownComponent
    case FORMATTER_TYPES.NUMBER:
      return NumberComponent
    case FORMATTER_TYPES.TEXT:
    case FORMATTER_TYPES.PLAINTEXT:
    case FORMATTER_TYPES.LONGTEXT:
      return TextComponent
    default:
      return TextComponent
  }
}

/** Create cell editor based on editor type with proper AG Grid integration. */
export const createCellEditor = (
  editor?: string,
  params?: GridEditorParams
): CellEditorComponent | undefined => {
  if (!editor) {
    return undefined
  }

  switch (editor) {
    case EDITOR_TYPES.INPUT:
      return AG_EDITORS.TEXT
    case EDITOR_TYPES.TEXTAREA:
      return AG_EDITORS.LARGE_TEXT
    case EDITOR_TYPES.NUMBER:
      return AG_EDITORS.NUMBER
    case EDITOR_TYPES.CHECKBOX:
      return AG_EDITORS.CHECKBOX
    case EDITOR_TYPES.DATE:
      return FacadeDateCellEditor
    case EDITOR_TYPES.TIME:
      return FacadeTimeCellEditor
    case EDITOR_TYPES.DATETIME:
      return FacadeDateTimeCellEditor
    case EDITOR_TYPES.TEL:
      return FacadeTelCellEditor
    case EDITOR_TYPES.SELECT:
    case EDITOR_TYPES.LIST:
      return params?.multiple ? AG_EDITORS.RICH_SELECT : AG_EDITORS.SELECT
    case EDITOR_TYPES.COMBOBOX:
    case EDITOR_TYPES.TAGS:
    case EDITOR_TYPES.STEPPER:
    case EDITOR_TYPES.DROPDOWN:
      return AG_EDITORS.RICH_SELECT
    default:
      return undefined
  }
}

const displayCellValue = (value: unknown): unknown =>
  isRawDisplayCell(value)
    ? extractEditorDisplayValue(value)
    : extractHighlight(value).value

export const extractRawEditorValue = (params: GridEditorCellParams): unknown =>
  extractEditorValue(params)

type EditorValidationParams = {
  readonly value: unknown
  readonly internalErrors: readonly string[] | null
}

const isEmptyEditorValue = (value: unknown): boolean =>
  isParsedEditorValue(value)
    ? isEmptyEditorValue(value.__facadeGridRawValue)
    : value === null ||
      value === undefined ||
      (isString(value) && value.trim().length === 0) ||
      (Array.isArray(value) && value.length === 0)

const requiredEditorValidation = (
  column: GridLeafColumn
): Readonly<Record<string, unknown>> =>
  column.editorParams?.required === true
    ? {
        getValidationErrors: ({
          value,
          internalErrors
        }: EditorValidationParams): string[] | null => {
          const errors = [
            ...(internalErrors ?? []),
            ...(isEmptyEditorValue(value) ? ['Enter a value'] : [])
          ]
          return errors.length > 0 ? errors : null
        }
      }
    : {}

/** Extract numeric value for AG-Grid number editor with comprehensive type handling */
const createNumberEditorParams =
  (column: GridLeafColumn) =>
  (params: GridEditorCellParams): Record<string, unknown> => {
    const value = extractRawEditorValue(params)

    // Convert to number or use undefined as fallback for empty/invalid values (AG-Grid convention)
    const numericValue =
      value === null || value === undefined || value === ''
        ? undefined
        : typeof value === 'number'
          ? value
          : Number.parseFloat(String(value))

    return {
      value: Number.isNaN(numericValue) ? undefined : numericValue,
      min:
        typeof column.editorParams?.min === 'number'
          ? column.editorParams.min
          : undefined,
      max:
        typeof column.editorParams?.max === 'number'
          ? column.editorParams.max
          : undefined,
      precision:
        typeof column.editorParams?.precision === 'number'
          ? column.editorParams.precision
          : undefined,
      ...requiredEditorValidation(column)
    }
  }

const createPrimitiveEditorParams =
  (column: GridLeafColumn): CellEditorParamsFunction =>
  (params) => ({
    ...column.editorParams,
    value: extractRawEditorValue(params),
    ...requiredEditorValidation(column)
  })

const extractCellLabel = (cellData: unknown): string | undefined =>
  isPlainObject(cellData) && isString(cellData.label)
    ? cellData.label
    : undefined

/** Extract badge values array from cell data's editorParams */
const extractBadgeValues = (cellData: unknown): readonly BadgeValueItem[] =>
  readArrayPath<BadgeValueItem>(cellData, ['editorParams', 'values'])

/** Extract stepper values array from cell data's editorParams */
const extractStepperValues = (cellData: unknown): readonly StepperValueItem[] =>
  readArrayPath<StepperValueItem>(cellData, ['editorParams', 'values'])

const createStepperRendererWithValues = (
  stepperValues: readonly StepperValueItem[]
) => {
  return class extends StepperRichSelectRenderer {
    getStepperValues(): readonly StepperValueItem[] {
      return stepperValues
    }
  }
}

/** Create stepper editor params with values and initial label */
const createStepperEditorParams = (
  column: GridLeafColumn,
  params: GridEditorCellParams
): Record<string, unknown> => {
  const cellData = params.data?.[params.column?.getColId() ?? '']
  const stepperValues = extractStepperValues(cellData)

  if (stepperValues.length === 0) {
    return createDropdownEditorParams(column, params)
  }

  const textValues = createTextValues(cellData)
  const currentValue = extractCellLabel(cellData)

  return {
    values: textValues,
    value: currentValue,
    cellRenderer: createStepperRendererWithValues(stepperValues),
    cellHeight: 32,
    allowTyping: true,
    filterList: true,
    highlightMatch: true,
    searchType: 'matchAny'
  }
}

/**
 * Create simple badge dropdown renderer factory with closure over badge values
 * Returns a renderer class that has access to the badge metadata
 * Uses SimpleBadgeDropdownRenderer for cleaner dropdown UX (icon + text only)
 */
const createBadgeRendererWithValues = (
  badgeValues: readonly BadgeValueItem[]
) => {
  return class extends SimpleBadgeDropdownRenderer {
    getBadgeValues(): readonly BadgeValueItem[] {
      return badgeValues
    }
  }
}

/** Create badge editor params with label values, initial label, and custom renderer */
const createBadgeEditorParams = (
  params: GridEditorCellParams
): {
  values: readonly string[]
  value?: string
  cellRenderer: typeof SimpleBadgeDropdownRenderer
  cellHeight?: number
  allowTyping: boolean
  filterList: boolean
  highlightMatch: boolean
  searchType: 'match' | 'matchAny'
} => {
  const colId = params.column?.getColId() ?? ''
  const cellData = params.data?.[colId]

  const currentValue = extractCellLabel(cellData)

  // Extract full badge values from cell data's editorParams
  let badgeValues = extractBadgeValues(cellData)

  // IMPORTANT: Ensure current badge data is included in badge values array
  // This handles cases where current status is not in allowed update list (permissions)
  if (
    cellData &&
    typeof cellData === 'object' &&
    'label' in cellData &&
    'value' in cellData
  ) {
    const currentBadge: BadgeValueItem = {
      value: String((cellData as BadgeCellData).value),
      label: String((cellData as BadgeCellData).label),
      icon: (cellData as BadgeCellData).icon,
      color: (cellData as BadgeCellData).color
    }

    // Check if current badge is already in the values array
    const hasCurrentBadge = badgeValues.some(
      (item) =>
        item.label === currentBadge.label || item.value === currentBadge.value
    )

    // If current badge is not in allowed updates, prepend it to the array
    if (!hasCurrentBadge) {
      badgeValues = [currentBadge, ...badgeValues]
    }
  }

  // Map to label strings for dropdown selection (AG Grid requirement)
  const labelValues = badgeValues.map((item) => item.label)

  return {
    values: labelValues,
    value: currentValue,
    cellRenderer: createBadgeRendererWithValues(badgeValues),
    cellHeight: 32, // Set dropdown item height in pixels (adjust as needed)
    allowTyping: true,
    filterList: true,
    highlightMatch: true,
    searchType: 'matchAny'
  }
}

/** Create column-configured dropdown params that retain raw option values. */
const createDropdownEditorParams = (
  column: GridLeafColumn,
  params: GridEditorCellParams
): Record<string, unknown> => {
  const options = column.editorParams?.values ?? []
  const selected = hydrateSelectedOptions(
    options,
    extractRawEditorValue(params),
    displayEditorValue(params)
  )[0]

  return {
    values: [...options],
    value: selected,
    allowTyping: true,
    filterList: true,
    highlightMatch: true,
    searchType: 'matchAny',
    formatValue: formatRichSelectValue,
    parseValue: parseRichSelectEditorValue
  }
}

const optionForValue = (
  options: readonly EditorParamValue[],
  value: unknown
): EditorParamValue | undefined =>
  options.find((option) => option.value === value)

const optionValue = (value: unknown): unknown =>
  isPlainObject(value) && hasProp(value, 'value') ? value.value : value

const createStaticSelectValueFormatter =
  (
    column: GridLeafColumn
  ): ((
    params: ValueFormatterParams<Record<string, unknown>, unknown>
  ) => string) =>
  ({ value }) => {
    if (isEditorValueCell(value) && hasProp(value, 'value')) {
      return String(value.value ?? '')
    }

    const rawValue = optionValue(value)
    const option = optionForValue(column.editorParams?.values ?? [], rawValue)

    return option
      ? (option.label ?? option.title ?? String(option.value))
      : String(rawValue ?? '')
  }

const createDateValueFormatter = (): ((
  params: ValueFormatterParams<Record<string, unknown>, unknown>
) => string) => {
  const settings = readCompanyFormatSettings()

  return ({ value }) => {
    const displayValue = displayCellValue(value)

    return isString(displayValue)
      ? formatDate(displayValue, settings)
      : String(displayValue ?? '')
  }
}

const createColumnValueFormatter = (
  column: GridLeafColumn
):
  | ((params: ValueFormatterParams<Record<string, unknown>, unknown>) => string)
  | undefined => {
  if (isDateEditor(column.editor)) {
    return createDateValueFormatter()
  }

  return (isSelectEditor(column.editor) &&
    column.editorParams?.multiple !== true) ||
    ((isDropdownEditor(column.editor) || isStepperEditor(column.editor)) &&
      column.editorParams?.values !== undefined &&
      column.formatter !== FORMATTER_TYPES.BADGE &&
      column.formatter !== FORMATTER_TYPES.STEPPER)
    ? createStaticSelectValueFormatter(column)
    : undefined
}

const displayEditorValue = (params: GridEditorCellParams): unknown => {
  const cellData = params.data?.[params.column.getColId()]
  return isEditorValueCell(cellData)
    ? extractEditorDisplayValue(cellData)
    : params.value
}

const hydrateSelectedOptions = (
  options: readonly EditorParamValue[],
  rawValue: unknown,
  displayValue: unknown
): readonly EditorParamValue[] => {
  const rawValues = Array.isArray(rawValue) ? rawValue : [rawValue]
  const displayValues = Array.isArray(displayValue)
    ? displayValue
    : [displayValue]

  return pipe(
    rawValues,
    filter(isEditorScalar),
    map(
      (selected, index) =>
        optionForValue(options, selected) ?? {
          value: selected,
          label: isString(displayValues[index])
            ? displayValues[index]
            : String(selected)
        }
    )
  )
}

const formatRichSelectValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return map(value, (item) =>
      isPlainObject(item) && hasProp(item, 'value')
        ? String(
            (hasProp(item, 'label') && item.label) ||
              (hasProp(item, 'title') && item.title) ||
              item.value
          )
        : String(item)
    ).join(', ')
  }

  return isPlainObject(value) && hasProp(value, 'value')
    ? String(
        (hasProp(value, 'label') && value.label) ||
          (hasProp(value, 'title') && value.title) ||
          value.value
      )
    : String(value ?? '')
}

const CLEAR_COMBOBOX_OPTION: ClearEditorOption = {
  label: '—',
  value: null
}

const isClearEditorOption = (value: unknown): value is ClearEditorOption =>
  isPlainObject(value) &&
  hasProp(value, 'value') &&
  value.value === null &&
  hasProp(value, 'label') &&
  value.label === CLEAR_COMBOBOX_OPTION.label

const resolveRichSelectValue = (
  value: unknown,
  options: readonly EditorParamValue[]
): unknown => {
  if (Array.isArray(value)) {
    return map(value, (item) => resolveRichSelectValue(item, options))
  }

  if (isPlainObject(value) && hasProp(value, 'value')) {
    return value
  }

  return optionForValue(options, value) ?? value
}

const createSelectEditorParams =
  (column: GridLeafColumn): CellEditorParamsFunction =>
  (params) => {
    const options = column.editorParams?.values ?? []
    const value = extractRawEditorValue(params)

    if (column.editorParams?.multiple !== true) {
      const values = map(options, (option) => option.value)
      const isOptional = column.editorParams?.required !== true
      // The native editor treats null as missing and selects values[0].
      // Use the existing empty-string clear sentinel for optional selects.
      const selectableValues =
        isOptional && !values.includes('') ? ['', ...values] : values

      return {
        values: selectableValues,
        value: isOptional && value == null ? '' : value,
        valueListMaxWidth: SELECT_LIST_WIDTH,
        ...requiredEditorValidation(column)
      }
    }

    const selectedOptions = hydrateSelectedOptions(
      options,
      value,
      displayEditorValue(params)
    )

    return {
      values: [...options],
      value: selectedOptions,
      multiSelect: true,
      allowTyping: true,
      filterList: true,
      highlightMatch: true,
      searchType: 'matchAny',
      formatValue: formatRichSelectValue,
      parseValue: parseRichSelectEditorValue,
      valueListMaxWidth: SELECT_LIST_WIDTH,
      ...requiredEditorValidation(column)
    }
  }

type LazyOptionPageRequest = {
  readonly search: string
  readonly startRow: number
  readonly endRow: number
}

type LazyOptionPage = {
  readonly values: readonly EditorParamValue[]
  readonly lastRow: number
}

const isEditorScalar = (value: unknown): value is EditorScalar =>
  isString(value) ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  typeof value === 'boolean'

const readPositiveIntegerParam = (
  value: unknown,
  name: 'pageSize' | 'timeout'
): number | undefined => {
  if (value === undefined) {
    return undefined
  }

  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    throw new TypeError(`Invalid grid editor ${name}`)
  }

  return value
}

const isEditorParamValue = (value: unknown): value is EditorParamValue =>
  isPlainObject(value) &&
  hasProp(value, 'value') &&
  isEditorScalar(value.value) &&
  (!hasProp(value, 'label') ||
    value.label === undefined ||
    isString(value.label)) &&
  (!hasProp(value, 'title') ||
    value.title === undefined ||
    isString(value.title)) &&
  (!hasProp(value, 'icon') ||
    value.icon === undefined ||
    isString(value.icon)) &&
  (!hasProp(value, 'color') ||
    value.color === undefined ||
    isString(value.color))

const parseLazyOptionPage = (reply: unknown): LazyOptionPage => {
  if (!isOptionPageReply(reply)) {
    throw new TypeError('Invalid grid editor option response')
  }

  const values = filter(reply.result, isEditorParamValue)
  if (values.length !== reply.result.length) {
    throw new TypeError('Invalid grid editor option')
  }

  return { values, lastRow: reply.size }
}

export const loadEditorOptionsPage = async (
  column: GridLeafColumn,
  params: GridEditorCellParams,
  request: LazyOptionPageRequest
): Promise<LazyOptionPage> => {
  const loadEvent = column.editorParams?.loadEvent
  const hook = params.context?.hook

  if (!(loadEvent && hook)) {
    throw new TypeError('Lazy grid editor requires a load event and hook')
  }

  const requestedSize = Math.max(request.endRow - request.startRow, 1)
  const pageSize =
    readPositiveIntegerParam(column.editorParams?.pageSize, 'pageSize') ??
    requestedSize
  const timeout = readPositiveIntegerParam(
    column.editorParams?.timeout,
    'timeout'
  )
  const page = Math.floor(request.startRow / pageSize)
  // Row context: `loadParams` is column-level, but a per-row editor (e.g. task
  // assignees, whose candidates depend on that row's project) needs to tell the
  // server which record it is editing.
  const payload = buildOptionPagePayload(
    { filter: request.search, page, pageSize },
    { ...(column.editorParams?.loadParams ?? {}), rowId: params.data?.id },
    request.startRow
  )
  const reply = await pushEventWithCallback(
    hook,
    loadEvent,
    payload,
    params.context?.target ?? undefined,
    timeout
  )

  return parseLazyOptionPage(reply)
}

type LazyRichSelectPage = {
  readonly values: readonly RichSelectOption[]
  readonly lastRow: number
}

const loadComboboxOptionsPage = async (
  column: GridLeafColumn,
  params: GridEditorCellParams,
  request: LazyOptionPageRequest,
  clearOption: ClearEditorOption | undefined
): Promise<LazyRichSelectPage> => {
  const firstPage = request.startRow === 0
  const includeClearOption = clearOption !== undefined && request.search === ''
  // The client-only clear row shifts every subsequent virtual row by one.
  const optionRequest =
    includeClearOption && !firstPage
      ? {
          ...request,
          startRow: request.startRow - 1,
          endRow: request.endRow - 1
        }
      : request
  const page = await loadEditorOptionsPage(column, params, optionRequest)

  if (!includeClearOption) {
    return page
  }

  return {
    values: firstPage ? [clearOption, ...page.values] : page.values,
    lastRow: page.lastRow + 1
  }
}

const createComboboxEditorParams =
  (column: GridLeafColumn): CellEditorParamsFunction =>
  (params) => {
    const multiple =
      column.editor === EDITOR_TYPES.TAGS ||
      column.editorParams?.multiple === true
    const clearOption =
      !multiple && column.editorParams?.required !== true
        ? CLEAR_COMBOBOX_OPTION
        : undefined
    const options = column.editorParams?.values ?? []
    const rawValue = extractRawEditorValue(params)
    const selectedOptions = hydrateSelectedOptions(
      options,
      rawValue,
      displayEditorValue(params)
    )
    // AG Grid can reapply a scalar edit value after the option page loads.
    // Retain that editor's options so the scalar always resolves back to its label.
    let resolvedOptions = [...selectedOptions, ...options]
    const pageSize = readPositiveIntegerParam(
      column.editorParams?.pageSize,
      'pageSize'
    )

    const lazyValues = column.editorParams?.loadEvent
      ? {
          valuesPage: async (request: LazyOptionPageRequest) => {
            const page = await loadComboboxOptionsPage(
              column,
              params,
              request,
              clearOption
            )
            resolvedOptions = [
              ...filter(page.values, isEditorParamValue),
              ...resolvedOptions
            ]
            return page
          },
          valuesPageSize: pageSize
        }
      : {
          values: clearOption ? [clearOption, ...options] : [...options]
        }
    const tagsRenderer = isTagsEditor(column.editor)
      ? { cellRenderer: BadgeComponent, cellHeight: 32 }
      : {}

    return {
      ...lazyValues,
      ...tagsRenderer,
      value: multiple ? selectedOptions : selectedOptions[0],
      multiSelect: multiple,
      allowTyping: true,
      filterList: true,
      filterListAsync: Boolean(column.editorParams?.loadEvent),
      highlightMatch: true,
      searchType: 'matchAny',
      formatValue: (value: unknown) =>
        formatRichSelectValue(resolveRichSelectValue(value, resolvedOptions)),
      parseValue: (value: unknown) =>
        isClearEditorOption(value)
          ? parsedEditorValue('', null)
          : parseRichSelectEditorValue(
              resolveRichSelectValue(value, resolvedOptions)
            ),
      valueListMaxWidth: SELECT_LIST_WIDTH,
      ...requiredEditorValidation(column)
    }
  }

/** Create rich select editor params for column */
const createRichSelectParams = (
  column: GridLeafColumn
): CellEditorParamsFunction => {
  return (params) => {
    if (isStepperEditor(column.editor)) {
      return {
        ...createStepperEditorParams(column, params),
        ...requiredEditorValidation(column)
      }
    }

    if (isDropdownEditor(column.editor)) {
      // Check if this is a badge column with dropdown editor
      if (isBadgeFormatter(column.formatter)) {
        return {
          ...createBadgeEditorParams(params),
          ...requiredEditorValidation(column)
        }
      }

      // Regular dropdown (non-badge)
      return {
        ...createDropdownEditorParams(column, params),
        ...requiredEditorValidation(column)
      }
    }

    if (isSelectEditor(column.editor)) {
      return createSelectEditorParams(column)(params)
    }

    if (isComboboxEditor(column.editor) || isTagsEditor(column.editor)) {
      return createComboboxEditorParams(column)(params)
    }

    return { values: [] }
  }
}

/** Cell editor params based on editor type with dynamic configuration */
export const cellEditorParams = (
  column: GridLeafColumn
): CellEditorParamsFunction | undefined => {
  // Unified rich select editor handling (stepper and dropdown)
  if (isRichSelectEditor(column.editor, column.editorParams)) {
    return createRichSelectParams(column)
  }

  // For number editors, return the numeric value extractor function
  if (isNumberEditor(column.editor)) {
    return createNumberEditorParams(column)
  }

  if (isSelectEditor(column.editor)) {
    return createSelectEditorParams(column)
  }

  if (column.editor && createCellEditor(column.editor, column.editorParams)) {
    return createPrimitiveEditorParams(column)
  }

  // No special params needed for other editor types
  return undefined
}

/** Header component params with icon support */
export const headerComponentParams = (
  column: GridLeafColumn
): HeaderComponentParams => ({
  innerHeaderComponent: HeaderComponent,
  innerHeaderComponentParams: {
    icon: column.titleFormatterParams?.icon
  }
})

/**
 * Select cell renderer based on row type and formatter
 * Uses plain text renderer for pinned bottom rows (summary rows)
 * Uses formatter-specific renderer for regular rows
 */
export const getCellRendererForRow = (
  column: GridLeafColumn,
  params: ICellRendererParams
): CellRendererSelection => {
  // Use plain text for pinned bottom rows (summary/aggregation rows)
  if (params.node?.rowPinned === ROW_POSITIONS.BOTTOM) {
    return { component: TextComponent }
  }

  // Use the formatter-specific renderer for regular rows
  const cellValue = params.data?.[column.field]
  const formatter = resolveCellFormatter(column.formatter, cellValue)

  return { component: selectCellRenderer(formatter) }
}

/** Tooltip value getter that shows explanatory tooltips for pinned bottom rows */
export const tooltipValueGetter =
  (column: GridLeafColumn): TooltipValueGetter =>
  (params) => {
    // Show explanatory tooltip only for pinned bottom rows (summary data)
    if (params.node?.rowPinned === 'bottom') {
      return column.bottomCalcFormatterParams?.tooltip ?? null
    }

    // No tooltip for regular data rows (extensible for future cell tooltips)
    return null
  }

const isEditableCell = (
  column: GridLeafColumn,
  params: EditableCellParams | EditableCellClassParams,
  readonly: boolean
): boolean => {
  if (readonly || params.context?.readonly === true) {
    return false
  }

  if (params.node?.rowPinned === ROW_POSITIONS.BOTTOM) {
    return false
  }

  const cellData = params.data?.[column.field]
  const hasCellEditable =
    isPlainObject(cellData) &&
    hasProp(cellData, 'editable') &&
    typeof cellData.editable === 'boolean'
  const cellEditable = hasCellEditable && cellData.editable === true

  if (
    column.editable === false ||
    (hasCellEditable && cellData.editable === false)
  ) {
    return false
  }

  return column.editable === true || cellEditable
}

// Reserve space for inline controls before editing, including saved narrow columns.
const columnSizeLimits = (
  column: GridLeafColumn
): Pick<GridLeafColumn, 'minWidth' | 'maxWidth'> => {
  const editorMinimum =
    column.editable !== false && column.editor !== undefined
      ? (EDITOR_MIN_WIDTHS[column.editor] ?? 0)
      : 0
  const minWidth = editorMinimum
    ? Math.max(column.minWidth ?? 0, editorMinimum)
    : column.minWidth
  const maxWidth =
    editorMinimum && column.maxWidth !== undefined
      ? Math.max(column.maxWidth, minWidth ?? editorMinimum)
      : column.maxWidth

  return { minWidth, maxWidth }
}

/** Transform grid column to AG-Grid column definition with comprehensive configuration */
export const transformToColDef = (
  column: GridLeafColumn,
  readonly = false
): ColDef<Record<string, unknown>, unknown> => {
  const editor = createCellEditor(column.editor, column.editorParams)

  return {
    field: column.field,
    context: { sortField: column.sortField },
    headerName: column.title,
    type: column.align === 'right' ? 'rightAligned' : undefined,
    width: column.width,
    ...columnSizeLimits(column),
    flex: column.flex,
    pinned: column.pinned,
    hide: !column.visible,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    valueGetter: (params) => {
      const stagedValue = readStagedEditorValue(params.data, column.field)

      if (stagedValue) {
        return stagedValue.__facadeGridDisplayValue
      }

      const cellValue = params.data?.[column.field]
      return column.formatter === FORMATTER_TYPES.MIXED &&
        hasFormatterProperty(cellValue)
        ? cellValue
        : displayCellValue(cellValue)
    },
    valueSetter: editor
      ? (params): boolean =>
          setEditorCellValue(
            params.data,
            column.field,
            params.oldValue,
            params.newValue
          )
      : undefined,
    cellStyle: (params) => {
      // Read original value from row data to access highlight info
      const cellValue = params.data?.[column.field]
      return getCellHighlightStyle(cellValue)
    },
    // Columns without an editor can never be editable — use a static value to
    // avoid a per-cell function call multiplied across all rows on every render.
    editable: editor
      ? (params): boolean => isEditableCell(column, params, readonly)
      : false,
    // Likewise, the editable-cell class rule is deterministic from column config.
    // Skip the per-cell function entirely for plain (non-editor) columns.
    cellClassRules: editor
      ? {
          'editable-cell': (params): boolean =>
            isEditableCell(column, params, readonly)
        }
      : {},
    cellEditor: editor,
    cellEditorParams: cellEditorParams(column),
    valueFormatter: createColumnValueFormatter(column),
    headerComponentParams: headerComponentParams(column),
    cellRendererSelector: (params) => getCellRendererForRow(column, params),
    // Leave unloaded rows and failed requests to AG Grid's native renderer.
    loadingCellRendererSelector: (
      params: ILoadingCellRendererParams<
        Record<string, unknown>,
        unknown,
        unknown
      >
    ) =>
      params.deferRender
        ? {
            component: CellPlaceholder,
            params: {
              formatter: column.formatter,
              align: column.align
            } satisfies CellPlaceholderParams
          }
        : undefined,
    cellRendererParams: {
      // Show the matching skeleton until scrolling stops, then build the cell.
      deferRender: (DEFERRED_RENDER_FORMATTERS as readonly string[]).includes(
        column.formatter
      ),
      editorParams: column.editorParams, // Pass column config to renderer for value normalization
      formatterType: column.formatter // Store formatter type for keyboard navigation
    },
    tooltip: tooltipValueGetter(column)
  }
}

const transformToColGroupDef = (
  column: GridGroupColumn,
  readonly = false
): ColGroupDef<Record<string, unknown>> => ({
  headerName: column.title,
  ...(column.groupId ? { groupId: column.groupId } : {}),
  children: [
    ...pipe(
      column.children,
      map((child) => transformToColumnDefinition(child, readonly))
    )
  ]
})

export const transformToColumnDefinition = (
  column: GridColumn,
  readonly = false
):
  | ColDef<Record<string, unknown>, unknown>
  | ColGroupDef<Record<string, unknown>> =>
  isGridGroupColumn(column)
    ? transformToColGroupDef(column, readonly)
    : transformToColDef(column, readonly)

/** Type guard to check if column has pinned calculation data */
const hasPinnedData = (column: GridLeafColumn): boolean =>
  column.bottomCalc === 'predefined' &&
  column.bottomCalcParams?.value !== undefined

/** Pinned bottom row data from columns with bottomCalc values using functional composition */
export const pinnedBottomData = (
  columns: readonly GridColumn[]
): Record<string, unknown> => {
  const filteredColumns = filter(flattenGridColumns(columns), hasPinnedData)
  const entries = map(
    filteredColumns,
    (column) => [column.field, column.bottomCalcParams?.value] as const
  )
  return fromEntries(entries) as Record<string, unknown>
}

/** Create drag handle column for row reordering functionality */
export const createDragHandleColumn = (readonly = false): ColDef => ({
  field: '__dragHandle',
  headerName: '',
  width: 40,
  minWidth: 40,
  maxWidth: 40,
  resizable: false,
  sortable: false,
  editable: false,
  filter: false,
  pinned: 'left',
  suppressMovable: true,
  suppressNavigable: true,
  suppressFillHandle: true,
  suppressHeaderMenuButton: true,
  suppressHeaderContextMenu: true,
  hide: readonly,
  cellClass: readonly
    ? 'ag-row-drag-cell ag-row-drag-disabled'
    : 'ag-row-drag-cell',
  cellRenderer: RowDraggerComponent,
  cellRendererParams: { readonly }
})

/** Enable native headers only where a bound grid declares a query mapping. */
export const configureColumnSorting = (
  columns: readonly (ColDef | ColGroupDef)[],
  bound: boolean
): readonly (ColDef | ColGroupDef)[] =>
  columns.map((column) =>
    'children' in column
      ? {
          ...column,
          children: [...configureColumnSorting(column.children, bound)]
        }
      : {
          ...column,
          sortable: bound && gridSortField(column.context) !== undefined,
          // Bound grids display backend ordering even with a client-side row model.
          ...(bound ? { comparator: (): number => 0 } : {})
        }
  )

/** Process server columns without enabling sorting in standalone grids. */
export const processColumns = (
  columns: readonly GridColumn[],
  movableRows = false,
  readonly = false
): readonly (ColDef | ColGroupDef)[] => {
  const processedColumns = pipe(
    columns,
    map((column) => transformToColumnDefinition(column, readonly))
  )

  // Inject drag handle column as first column when row reordering is enabled
  return movableRows
    ? [createDragHandleColumn(readonly), ...processedColumns]
    : processedColumns
}

// Export types for external use
// Column processing function type for consistent threading through event handlers
export type ProcessColumnsFunction = (
  columns: readonly GridColumn[],
  movableRows?: boolean,
  readonly?: boolean
) => readonly (ColDef | ColGroupDef)[]

export type {
  BadgeCellData,
  BadgeValueItem,
  CellEditorComponent,
  CellEditorParamsFunction,
  CellRendererSelection,
  CellRendererSelectorFunction,
  EditorParamValue,
  EditorScalar,
  GridColumn,
  GridEditorParams,
  GridGroupColumn,
  GridLeafColumn,
  HeaderComponentParams,
  ObjectWithFormatter,
  ObjectWithValue,
  TooltipValueGetter
}
